import { db } from '../firebase/config';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  increment,
  Timestamp 
} from 'firebase/firestore';
import { nanoid } from 'nanoid';

// Types of items that can be shared
export type TrackableItemType = 'event' | 'asset' | 'post' | 'qrcode';

// Social media platforms
export type SocialPlatform = 'facebook' | 'twitter' | 'linkedin' | 'instagram' | 'email' | 'whatsapp' | 'direct';

// Share record
export interface ShareRecord {
  id: string;
  itemId: string;
  itemType: TrackableItemType;
  platform: SocialPlatform;
  shareDate: Timestamp;
  shareUrl: string;
  trackingId: string;
  userId?: string;
  userName?: string;
  organizationId: string;
  eventId?: string;
  metadata?: Record<string, any>;
}

// Click record
export interface ClickRecord {
  id: string;
  shareId: string;
  trackingId: string;
  clickDate: Timestamp;
  referrer?: string;
  userAgent?: string;
  ipHash?: string; // Hashed IP for privacy
  location?: {
    country?: string;
    region?: string;
    city?: string;
  };
  metadata?: Record<string, any>;
}

// Analytics summary
export interface AnalyticsSummary {
  totalShares: number;
  totalClicks: number;
  clickThroughRate: number;
  sharesByPlatform: Record<SocialPlatform, number>;
  clicksByPlatform: Record<SocialPlatform, number>;
  clicksByDate: Record<string, number>;
  sharesByDate: Record<string, number>;
  topPerformingItems: Array<{
    itemId: string;
    itemType: TrackableItemType;
    title?: string;
    shares: number;
    clicks: number;
    ctr: number;
  }>;
}

// Email-specific types for contact tracking
export interface EmailTrackingRecord {
  id: string;
  contactId: string;
  organizationId: string;
  emailType: 'event' | 'subscription' | 'trip';
  emailId: string; // ID of the specific email sent
  itemId: string; // ID of the event, subscription, or trip
  itemName: string; // Name of the event, subscription, or trip
  sentDate: Timestamp;
  trackingId: string;
  metadata?: Record<string, any>;
}

export interface EmailEngagementRecord {
  id: string;
  trackingId: string;
  contactId: string;
  type: 'open' | 'click' | 'conversion';
  timestamp: Timestamp;
  conversionType?: 'signup' | 'purchase' | 'rsvp';
  conversionValue?: number;
  referrer?: string;
  device?: string;
  metadata?: Record<string, any>;
}

export interface ContactEngagementSummary {
  contactId: string;
  totalEmails: number;
  totalOpens: number;
  totalClicks: number;
  totalConversions: number;
  openRate: number;
  clickRate: number;
  conversionRate: number;
  lastEngagement: Timestamp | null;
  emailsByType: Record<string, number>;
  conversionsByType: Record<string, number>;
  engagementTimeline: Array<{
    date: string;
    type: 'sent' | 'open' | 'click' | 'conversion';
    itemType: string;
    itemName: string;
  }>;
}

/**
 * Service for tracking shares and clicks
 */
const trackingService = {
  /**
   * Generate a unique trackable URL for sharing
   * 
   * @param baseUrl Base URL for the shared item
   * @param itemId ID of the item being shared
   * @param itemType Type of item being shared
   * @param platform Platform where the item is being shared
   * @returns Object containing the tracking ID and shareable URL
   */
  generateTrackableUrl: (
    baseUrl: string,
    itemId: string,
    itemType: TrackableItemType,
    platform: SocialPlatform
  ): { trackingId: string, shareUrl: string } => {
    // Generate a short unique ID for tracking
    const trackingId = nanoid(8);
    
    // Create the shareable URL with tracking parameter
    const shareUrl = new URL(baseUrl);
    shareUrl.searchParams.append('utm_source', platform);
    shareUrl.searchParams.append('utm_medium', 'social');
    shareUrl.searchParams.append('utm_campaign', itemType);
    shareUrl.searchParams.append('share_id', trackingId);
    
    return {
      trackingId,
      shareUrl: shareUrl.toString()
    };
  },
  
  /**
   * Record a share of an item
   * 
   * @param itemId ID of the item being shared
   * @param itemType Type of item being shared
   * @param platform Platform where the item is being shared
   * @param shareUrl The shareable URL
   * @param trackingId Tracking ID for this share
   * @param organizationId ID of the organization
   * @param eventId Optional event ID if the share is related to an event
   * @param userId Optional ID of the user who shared
   * @param userName Optional name of the user who shared
   * @returns The ID of the created share record
   */
  recordShare: async (
    itemId: string,
    itemType: TrackableItemType,
    platform: SocialPlatform,
    shareUrl: string,
    trackingId: string,
    organizationId: string,
    eventId?: string,
    userId?: string,
    userName?: string,
    metadata?: Record<string, any>
  ): Promise<string> => {
    try {
      // Create share record
      const shareRecord: Omit<ShareRecord, 'id'> = {
        itemId,
        itemType,
        platform,
        shareDate: Timestamp.now(),
        shareUrl,
        trackingId,
        organizationId,
        eventId,
        userId,
        userName,
        metadata
      };
      
      // Save to Firestore
      const sharesCollection = collection(db, 'shares');
      const shareDoc = await addDoc(sharesCollection, shareRecord);
      
      // Update item stats (if it has a stats subcollection)
      try {
        let statsDocRef;
        if (itemType === 'event' && eventId) {
          statsDocRef = doc(db, 'events', eventId, 'stats', 'sharing');
        } else if (itemType === 'asset' && eventId) {
          statsDocRef = doc(db, 'events', eventId, 'assets', itemId, 'stats', 'sharing');
        }
        
        if (statsDocRef) {
          // Try to get the doc first
          const statsDoc = await getDoc(statsDocRef);
          
          if (statsDoc.exists()) {
            // Update existing stats
            await updateDoc(statsDocRef, {
              [`shareCount.total`]: increment(1),
              [`shareCount.${platform}`]: increment(1),
              lastUpdated: Timestamp.now()
            });
          } else {
            // Create new stats document
            const initialStats = {
              shareCount: {
                total: 1,
                [platform]: 1
              },
              clickCount: {
                total: 0
              },
              created: Timestamp.now(),
              lastUpdated: Timestamp.now()
            };
            
            await updateDoc(statsDocRef, initialStats);
          }
        }
      } catch (statsErr) {
        console.error('Error updating item stats:', statsErr);
        // Continue even if stats update fails
      }
      
      return shareDoc.id;
    } catch (error) {
      console.error('Error recording share:', error);
      throw error;
    }
  },
  
  /**
   * Record a click on a shared item
   * 
   * @param trackingId Tracking ID from the URL
   * @param referrer Referrer URL
   * @param userAgent User agent string
   * @param ipHash Hashed IP address for privacy
   * @param location Optional location data
   * @returns The ID of the created click record
   */
  recordClick: async (
    trackingId: string,
    referrer?: string,
    userAgent?: string,
    ipHash?: string,
    location?: { country?: string, region?: string, city?: string },
    metadata?: Record<string, any>
  ): Promise<string> => {
    try {
      // Find the share record
      const sharesCollection = collection(db, 'shares');
      const shareQuery = query(sharesCollection, where('trackingId', '==', trackingId));
      const shareSnapshot = await getDocs(shareQuery);
      
      if (shareSnapshot.empty) {
        throw new Error(`No share found with tracking ID: ${trackingId}`);
      }
      
      const shareDoc = shareSnapshot.docs[0];
      const shareData = shareDoc.data() as ShareRecord;
      shareData.id = shareDoc.id;
      
      // Create click record
      const clickRecord: Omit<ClickRecord, 'id'> = {
        shareId: shareData.id,
        trackingId,
        clickDate: Timestamp.now(),
        referrer,
        userAgent,
        ipHash,
        location,
        metadata
      };
      
      // Save to Firestore
      const clicksCollection = collection(db, 'clicks');
      const clickDoc = await addDoc(clicksCollection, clickRecord);
      
      // Update item stats (if it has a stats subcollection)
      try {
        let statsDocRef;
        if (shareData.itemType === 'event' && shareData.eventId) {
          statsDocRef = doc(db, 'events', shareData.eventId, 'stats', 'sharing');
        } else if (shareData.itemType === 'asset' && shareData.eventId) {
          statsDocRef = doc(db, 'events', shareData.eventId, 'assets', shareData.itemId, 'stats', 'sharing');
        }
        
        if (statsDocRef) {
          // Update stats
          await updateDoc(statsDocRef, {
            'clickCount.total': increment(1),
            [`clickCount.${shareData.platform}`]: increment(1),
            lastUpdated: Timestamp.now()
          });
        }
      } catch (statsErr) {
        console.error('Error updating item stats:', statsErr);
        // Continue even if stats update fails
      }
      
      return clickDoc.id;
    } catch (error) {
      console.error('Error recording click:', error);
      throw error;
    }
  },
  
  /**
   * Get analytics for an event
   * 
   * @param eventId Event ID
   * @returns Analytics summary for the event
   */
  getEventAnalytics: async (eventId: string): Promise<AnalyticsSummary> => {
    try {
      // Initialize default summary
      const summary: AnalyticsSummary = {
        totalShares: 0,
        totalClicks: 0,
        clickThroughRate: 0,
        sharesByPlatform: {
          facebook: 0,
          twitter: 0,
          linkedin: 0,
          instagram: 0,
          email: 0,
          whatsapp: 0,
          direct: 0
        },
        clicksByPlatform: {
          facebook: 0,
          twitter: 0,
          linkedin: 0,
          instagram: 0,
          email: 0,
          whatsapp: 0,
          direct: 0
        },
        sharesByDate: {},
        clicksByDate: {},
        topPerformingItems: []
      };
      
      // Get all shares for this event
      const sharesCollection = collection(db, 'shares');
      const sharesQuery = query(
        sharesCollection, 
        where('eventId', '==', eventId),
        orderBy('shareDate', 'desc')
      );
      const sharesSnapshot = await getDocs(sharesQuery);
      
      // Item map to track performance by item
      const itemMap: Record<string, {
        itemId: string;
        itemType: TrackableItemType;
        title?: string;
        shares: number;
        clicks: number;
      }> = {};
      
      // Process shares
      const shareIds: string[] = [];
      sharesSnapshot.forEach(doc => {
        const shareData = doc.data() as ShareRecord;
        shareData.id = doc.id;
        shareIds.push(doc.id);
        
        // Update total shares
        summary.totalShares++;
        
        // Update shares by platform
        if (shareData.platform in summary.sharesByPlatform) {
          summary.sharesByPlatform[shareData.platform]++;
        }
        
        // Update shares by date
        const dateKey = new Date(shareData.shareDate.toDate()).toISOString().split('T')[0];
        summary.sharesByDate[dateKey] = (summary.sharesByDate[dateKey] || 0) + 1;
        
        // Update item map
        const itemKey = `${shareData.itemType}:${shareData.itemId}`;
        if (!itemMap[itemKey]) {
          itemMap[itemKey] = {
            itemId: shareData.itemId,
            itemType: shareData.itemType,
            title: shareData.metadata?.title,
            shares: 0,
            clicks: 0
          };
        }
        itemMap[itemKey].shares++;
      });
      
      // Get all clicks for shares of this event
      if (shareIds.length > 0) {
        const clicksCollection = collection(db, 'clicks');
        const clickBatches = [];
        
        // Firestore has limits on 'in' queries, so we need to batch
        for (let i = 0; i < shareIds.length; i += 10) {
          const batch = shareIds.slice(i, i + 10);
          clickBatches.push(batch);
        }
        
        // Process each batch
        for (const batch of clickBatches) {
          const clicksQuery = query(
            clicksCollection,
            where('shareId', 'in', batch),
            orderBy('clickDate', 'desc')
          );
          const clicksSnapshot = await getDocs(clicksQuery);
          
          // Process clicks
          clicksSnapshot.forEach(doc => {
            const clickData = doc.data() as ClickRecord;
            
            // Update total clicks
            summary.totalClicks++;
            
            // Find the associated share to get platform
            const shareDoc = sharesSnapshot.docs.find(d => d.id === clickData.shareId);
            if (shareDoc) {
              const shareData = shareDoc.data() as ShareRecord;
              
              // Update clicks by platform
              if (shareData.platform in summary.clicksByPlatform) {
                summary.clicksByPlatform[shareData.platform]++;
              }
              
              // Update item map clicks
              const itemKey = `${shareData.itemType}:${shareData.itemId}`;
              if (itemMap[itemKey]) {
                itemMap[itemKey].clicks++;
              }
            }
            
            // Update clicks by date
            const dateKey = new Date(clickData.clickDate.toDate()).toISOString().split('T')[0];
            summary.clicksByDate[dateKey] = (summary.clicksByDate[dateKey] || 0) + 1;
          });
        }
      }
      
      // Calculate click-through rate
      summary.clickThroughRate = summary.totalShares > 0 
        ? (summary.totalClicks / summary.totalShares) 
        : 0;
      
      // Create top performing items array
      summary.topPerformingItems = Object.values(itemMap)
        .map(item => ({
          ...item,
          ctr: item.shares > 0 ? (item.clicks / item.shares) : 0
        }))
        .sort((a, b) => b.clicks - a.clicks)
        .slice(0, 10);
      
      return summary;
    } catch (error) {
      console.error('Error getting event analytics:', error);
      throw error;
    }
  },
  
  /**
   * Get analytics for a specific asset
   * 
   * @param eventId Event ID
   * @param assetId Asset ID
   * @returns Analytics summary for the asset
   */
  getAssetAnalytics: async (eventId: string, assetId: string): Promise<AnalyticsSummary> => {
    try {
      // Initialize default summary
      const summary: AnalyticsSummary = {
        totalShares: 0,
        totalClicks: 0,
        clickThroughRate: 0,
        sharesByPlatform: {
          facebook: 0,
          twitter: 0,
          linkedin: 0,
          instagram: 0,
          email: 0,
          whatsapp: 0,
          direct: 0
        },
        clicksByPlatform: {
          facebook: 0,
          twitter: 0,
          linkedin: 0,
          instagram: 0,
          email: 0,
          whatsapp: 0,
          direct: 0
        },
        sharesByDate: {},
        clicksByDate: {},
        topPerformingItems: []
      };
      
      // Get all shares for this asset
      const sharesCollection = collection(db, 'shares');
      const sharesQuery = query(
        sharesCollection,
        where('eventId', '==', eventId),
        where('itemType', '==', 'asset'),
        where('itemId', '==', assetId),
        orderBy('shareDate', 'desc')
      );
      const sharesSnapshot = await getDocs(sharesQuery);
      
      // Process shares
      const shareIds: string[] = [];
      sharesSnapshot.forEach(doc => {
        const shareData = doc.data() as ShareRecord;
        shareData.id = doc.id;
        shareIds.push(doc.id);
        
        // Update total shares
        summary.totalShares++;
        
        // Update shares by platform
        if (shareData.platform in summary.sharesByPlatform) {
          summary.sharesByPlatform[shareData.platform]++;
        }
        
        // Update shares by date
        const dateKey = new Date(shareData.shareDate.toDate()).toISOString().split('T')[0];
        summary.sharesByDate[dateKey] = (summary.sharesByDate[dateKey] || 0) + 1;
      });
      
      // Get all clicks for shares of this asset
      if (shareIds.length > 0) {
        const clicksCollection = collection(db, 'clicks');
        const clickBatches = [];
        
        // Firestore has limits on 'in' queries, so we need to batch
        for (let i = 0; i < shareIds.length; i += 10) {
          const batch = shareIds.slice(i, i + 10);
          clickBatches.push(batch);
        }
        
        // Process each batch
        for (const batch of clickBatches) {
          const clicksQuery = query(
            clicksCollection,
            where('shareId', 'in', batch),
            orderBy('clickDate', 'desc')
          );
          const clicksSnapshot = await getDocs(clicksQuery);
          
          // Process clicks
          clicksSnapshot.forEach(doc => {
            const clickData = doc.data() as ClickRecord;
            
            // Update total clicks
            summary.totalClicks++;
            
            // Find the associated share to get platform
            const shareDoc = sharesSnapshot.docs.find(d => d.id === clickData.shareId);
            if (shareDoc) {
              const shareData = shareDoc.data() as ShareRecord;
              
              // Update clicks by platform
              if (shareData.platform in summary.clicksByPlatform) {
                summary.clicksByPlatform[shareData.platform]++;
              }
            }
            
            // Update clicks by date
            const dateKey = new Date(clickData.clickDate.toDate()).toISOString().split('T')[0];
            summary.clicksByDate[dateKey] = (summary.clicksByDate[dateKey] || 0) + 1;
          });
        }
      }
      
      // Calculate click-through rate
      summary.clickThroughRate = summary.totalShares > 0 
        ? (summary.totalClicks / summary.totalShares) 
        : 0;
      
      return summary;
    } catch (error) {
      console.error('Error getting asset analytics:', error);
      throw error;
    }
  },
  
  /**
   * Track an email sent to a contact
   * 
   * @param contactId ID of the contact
   * @param organizationId ID of the organization
   * @param emailType Type of email (event, subscription, trip)
   * @param itemId ID of the related item (event, subscription, trip)
   * @param itemName Name of the related item
   * @param metadata Optional additional data
   * @returns Object with tracking ID and the email tracking record ID
   */
  trackContactEmail: async (
    contactId: string,
    organizationId: string,
    emailType: 'event' | 'subscription' | 'trip',
    itemId: string,
    itemName: string,
    metadata?: Record<string, any>
  ): Promise<{ trackingId: string, recordId: string }> => {
    try {
      // Generate a unique tracking ID
      const trackingId = nanoid(10);
      
      // Create the email tracking record
      const emailRecord: Omit<EmailTrackingRecord, 'id'> = {
        contactId,
        organizationId,
        emailType,
        emailId: nanoid(8), // Generate a unique email ID
        itemId,
        itemName,
        sentDate: Timestamp.now(),
        trackingId,
        metadata
      };
      
      // Save to Firestore
      const emailTracksCollection = collection(db, 'organizations', organizationId, 'emailTracks');
      const emailTrackDoc = await addDoc(emailTracksCollection, emailRecord);
      
      // Update contact's email count in their stats
      try {
        const contactStatsRef = doc(db, 'organizations', organizationId, 'contacts', contactId, 'stats', 'engagement');
        const contactStatsDoc = await getDoc(contactStatsRef);
        
        if (contactStatsDoc.exists()) {
          await updateDoc(contactStatsRef, {
            totalEmails: increment(1),
            [`emailsByType.${emailType}`]: increment(1),
            lastEmailSent: Timestamp.now()
          });
        } else {
          // Create initial stats
          const initialStats = {
            totalEmails: 1,
            totalOpens: 0,
            totalClicks: 0,
            totalConversions: 0,
            emailsByType: {
              [emailType]: 1
            },
            opensByType: {},
            clicksByType: {},
            conversionsByType: {},
            lastEmailSent: Timestamp.now(),
            lastEngagement: null
          };
          
          // Create the document
          await updateDoc(contactStatsRef, initialStats);
        }
      } catch (statsErr) {
        console.error('Error updating contact email stats:', statsErr);
        // Continue even if stats update fails
      }
      
      return {
        trackingId,
        recordId: emailTrackDoc.id
      };
    } catch (error) {
      console.error('Error tracking contact email:', error);
      throw error;
    }
  },
  
  /**
   * Record an email engagement (open, click, conversion)
   * 
   * @param trackingId Tracking ID from the email
   * @param contactId ID of the contact
   * @param organizationId ID of the organization
   * @param engagementType Type of engagement
   * @param metadata Optional additional data
   * @returns The ID of the created engagement record
   */
  recordEmailEngagement: async (
    trackingId: string,
    contactId: string,
    organizationId: string,
    engagementType: 'open' | 'click' | 'conversion',
    conversionType?: 'signup' | 'purchase' | 'rsvp',
    conversionValue?: number,
    metadata?: Record<string, any>
  ): Promise<string> => {
    try {
      // Look up the original email tracking record
      const emailTracksCollection = collection(db, 'organizations', organizationId, 'emailTracks');
      const q = query(emailTracksCollection, where('trackingId', '==', trackingId));
      const emailTrackSnapshot = await getDocs(q);
      
      if (emailTrackSnapshot.empty) {
        throw new Error(`No email tracking record found for tracking ID: ${trackingId}`);
      }
      
      const emailTrackDoc = emailTrackSnapshot.docs[0];
      const emailTrack = emailTrackDoc.data() as EmailTrackingRecord;
      
      // Create the engagement record
      const engagementRecord: Omit<EmailEngagementRecord, 'id'> = {
        trackingId,
        contactId,
        type: engagementType,
        timestamp: Timestamp.now(),
        conversionType,
        conversionValue,
        referrer: metadata?.referrer,
        device: metadata?.device,
        metadata
      };
      
      // Save to Firestore
      const engagementsCollection = collection(db, 'organizations', organizationId, 'emailEngagements');
      const engagementDoc = await addDoc(engagementsCollection, engagementRecord);
      
      // Update contact's engagement stats
      try {
        const contactStatsRef = doc(db, 'organizations', organizationId, 'contacts', contactId, 'stats', 'engagement');
        const updates: Record<string, any> = {
          [`total${engagementType.charAt(0).toUpperCase() + engagementType.slice(1)}s`]: increment(1),
          lastEngagement: Timestamp.now()
        };
        
        // Add type-specific counters
        if (emailTrack.emailType) {
          updates[`${engagementType}sByType.${emailTrack.emailType}`] = increment(1);
        }
        
        // Add conversion-specific data
        if (engagementType === 'conversion' && conversionType) {
          updates[`conversionsByType.${conversionType}`] = increment(1);
          if (conversionValue) {
            updates.totalConversionValue = increment(conversionValue);
          }
        }
        
        await updateDoc(contactStatsRef, updates);
        
        // Also update the email tracking record
        await updateDoc(doc(emailTracksCollection, emailTrackDoc.id), {
          [`engagements.${engagementType}`]: true,
          [`engagementDates.${engagementType}`]: Timestamp.now()
        });
      } catch (statsErr) {
        console.error('Error updating contact engagement stats:', statsErr);
        // Continue even if stats update fails
      }
      
      return engagementDoc.id;
    } catch (error) {
      console.error('Error recording email engagement:', error);
      throw error;
    }
  },
  
  /**
   * Get analytics for a specific contact
   * 
   * @param organizationId Organization ID
   * @param contactId Contact ID
   * @returns Engagement summary for the contact
   */
  getContactAnalytics: async (
    organizationId: string,
    contactId: string
  ): Promise<ContactEngagementSummary> => {
    try {
      // Get all email tracks for this contact
      const emailTracksCollection = collection(db, 'organizations', organizationId, 'emailTracks');
      const emailTracksQuery = query(
        emailTracksCollection,
        where('contactId', '==', contactId),
        orderBy('sentDate', 'desc')
      );
      const emailTracksSnapshot = await getDocs(emailTracksQuery);
      
      // Get all engagements for this contact
      const engagementsCollection = collection(db, 'organizations', organizationId, 'emailEngagements');
      const engagementsQuery = query(
        engagementsCollection,
        where('contactId', '==', contactId),
        orderBy('timestamp', 'desc')
      );
      const engagementsSnapshot = await getDocs(engagementsQuery);
      
      // Process data
      const emailTracks = emailTracksSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as EmailTrackingRecord[];
      
      const engagements = engagementsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as EmailEngagementRecord[];
      
      // Count emails by type
      const emailsByType: Record<string, number> = {};
      emailTracks.forEach(track => {
        const type = track.emailType;
        emailsByType[type] = (emailsByType[type] || 0) + 1;
      });
      
      // Count conversions by type
      const conversionsByType: Record<string, number> = {};
      engagements
        .filter(eng => eng.type === 'conversion' && eng.conversionType)
        .forEach(eng => {
          const type = eng.conversionType as string;
          conversionsByType[type] = (conversionsByType[type] || 0) + 1;
        });
      
      // Build timeline
      const timeline: Array<{
        date: string;
        type: 'sent' | 'open' | 'click' | 'conversion';
        itemType: string;
        itemName: string;
      }> = [];
      
      // Add sent emails to timeline
      emailTracks.forEach(track => {
        timeline.push({
          date: track.sentDate.toDate().toISOString(),
          type: 'sent',
          itemType: track.emailType,
          itemName: track.itemName
        });
      });
      
      // Add engagements to timeline
      engagements.forEach(eng => {
        // Find the corresponding email
        const emailTrack = emailTracks.find(track => track.trackingId === eng.trackingId);
        if (emailTrack) {
          timeline.push({
            date: eng.timestamp.toDate().toISOString(),
            type: eng.type as any, // Cast to the expected type
            itemType: emailTrack.emailType,
            itemName: emailTrack.itemName
          });
        }
      });
      
      // Sort timeline by date
      timeline.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      
      // Calculate rates
      const totalEmails = emailTracks.length;
      const totalOpens = engagements.filter(eng => eng.type === 'open').length;
      const totalClicks = engagements.filter(eng => eng.type === 'click').length;
      const totalConversions = engagements.filter(eng => eng.type === 'conversion').length;
      
      const openRate = totalEmails > 0 ? (totalOpens / totalEmails) * 100 : 0;
      const clickRate = totalEmails > 0 ? (totalClicks / totalEmails) * 100 : 0;
      const conversionRate = totalEmails > 0 ? (totalConversions / totalEmails) * 100 : 0;
      
      // Find last engagement
      let lastEngagement: Timestamp | null = null;
      if (engagements.length > 0) {
        lastEngagement = engagements[0].timestamp;
      }
      
      return {
        contactId,
        totalEmails,
        totalOpens,
        totalClicks,
        totalConversions,
        openRate,
        clickRate,
        conversionRate,
        lastEngagement,
        emailsByType,
        conversionsByType,
        engagementTimeline: timeline
      };
    } catch (error) {
      console.error('Error getting contact analytics:', error);
      throw error;
    }
  },
  
  /**
   * Get analytics for all contacts in an organization
   * 
   * @param organizationId Organization ID
   * @returns Object with aggregated stats and individual contact summaries
   */
  getOrganizationEmailAnalytics: async (
    organizationId: string
  ): Promise<{
    totalEmails: number;
    totalOpens: number;
    totalClicks: number;
    totalConversions: number;
    averageOpenRate: number;
    averageClickRate: number;
    averageConversionRate: number;
    emailsByType: Record<string, number>;
    contactEngagements: ContactEngagementSummary[];
    topEngagedContacts: Array<ContactEngagementSummary & { contactName: string }>;
  }> => {
    try {
      // Get all email tracks for this organization
      const emailTracksCollection = collection(db, 'organizations', organizationId, 'emailTracks');
      const emailTracksSnapshot = await getDocs(emailTracksCollection);
      
      // Get all engagements for this organization
      const engagementsCollection = collection(db, 'organizations', organizationId, 'emailEngagements');
      const engagementsSnapshot = await getDocs(engagementsCollection);
      
      // Get contacts data
      const contactsCollection = collection(db, 'organizations', organizationId, 'contacts');
      const contactsSnapshot = await getDocs(contactsCollection);
      const contacts = contactsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Process data
      const emailTracks = emailTracksSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as EmailTrackingRecord[];
      
      const engagements = engagementsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as EmailEngagementRecord[];
      
      // Group by contact ID
      const contactIds = new Set<string>();
      emailTracks.forEach(track => contactIds.add(track.contactId));
      
      // Get individual contact analytics
      const contactEngagements: ContactEngagementSummary[] = [];
      const contactsWithAnalytics: Array<ContactEngagementSummary & { contactName: string }> = [];
      
      for (const contactId of Array.from(contactIds)) {
        const contact = contacts.find(c => c.id === contactId);
        const contactName = contact && 'firstName' in contact && 'lastName' in contact
          ? `${contact.firstName} ${contact.lastName}`
          : 'Unknown Contact';
        
        // Use a direct reference to the method to ensure 'this' binding
        const getContactAnalytics = trackingService.getContactAnalytics.bind(trackingService);
        const analytics = await getContactAnalytics(organizationId, contactId);
        contactEngagements.push(analytics);
        
        contactsWithAnalytics.push({
          ...analytics,
          contactName
        });
      }
      
      // Calculate organization-wide metrics
      const totalEmails = emailTracks.length;
      const totalOpens = engagements.filter(eng => eng.type === 'open').length;
      const totalClicks = engagements.filter(eng => eng.type === 'click').length;
      const totalConversions = engagements.filter(eng => eng.type === 'conversion').length;
      
      // Email types breakdown
      const emailsByType: Record<string, number> = {};
      emailTracks.forEach(track => {
        const type = track.emailType;
        emailsByType[type] = (emailsByType[type] || 0) + 1;
      });
      
      // Calculate averages
      const averageOpenRate = contactEngagements.length > 0
        ? contactEngagements.reduce((sum, item) => sum + item.openRate, 0) / contactEngagements.length
        : 0;
        
      const averageClickRate = contactEngagements.length > 0
        ? contactEngagements.reduce((sum, item) => sum + item.clickRate, 0) / contactEngagements.length
        : 0;
        
      const averageConversionRate = contactEngagements.length > 0
        ? contactEngagements.reduce((sum, item) => sum + item.conversionRate, 0) / contactEngagements.length
        : 0;
      
      // Sort contacts by engagement level (using a score based on opens, clicks, and conversions)
      const topEngagedContacts = [...contactsWithAnalytics]
        .sort((a, b) => {
          const scoreA = a.totalOpens + (a.totalClicks * 2) + (a.totalConversions * 5);
          const scoreB = b.totalOpens + (b.totalClicks * 2) + (b.totalConversions * 5);
          return scoreB - scoreA;
        })
        .slice(0, 10); // Get top 10
      
      return {
        totalEmails,
        totalOpens,
        totalClicks,
        totalConversions,
        averageOpenRate,
        averageClickRate,
        averageConversionRate,
        emailsByType,
        contactEngagements,
        topEngagedContacts
      };
    } catch (error) {
      console.error('Error getting organization email analytics:', error);
      throw error;
    }
  }
};

export default trackingService;
